// F-148 §4.8 manual quantity defaults (AC11). Provenance (`acceptedAuto`) is persisted and read,
// never inferred from arithmetic. Every function here is pure and returns a new line object.

import { RECEIVING_LIMITS, type ReceivingDraftLineInput } from '../types/stock-receiving';

export type ReceivingQuantityField =
  | 'expectedUnits'
  | 'acceptedUnits'
  | 'damagedUnits'
  | 'missingUnits'
  | 'packsExpected'
  | 'unitsPerPack';

type QuantityShape = Pick<
  ReceivingDraftLineInput,
  | 'unitKind'
  | 'packsExpected'
  | 'unitsPerPack'
  | 'unitsPerPackConfirmed'
  | 'expectedUnits'
  | 'acceptedUnits'
  | 'damagedUnits'
  | 'missingUnits'
  | 'acceptedAuto'
>;

/** `expected == null ? null : max(0, expected − (damaged ?? 0) − (missing ?? 0))` */
export function autoAcceptedUnits(
  line: Pick<QuantityShape, 'expectedUnits' | 'damagedUnits' | 'missingUnits'>,
): number | null {
  if (line.expectedUnits == null) return null;
  return Math.max(0, line.expectedUnits - (line.damagedUnits ?? 0) - (line.missingUnits ?? 0));
}

/** Pack lines derive expected from packs × units-per-pack; unit lines keep the typed expected. */
export function derivedExpectedUnits(
  line: Pick<QuantityShape, 'unitKind' | 'packsExpected' | 'unitsPerPack' | 'expectedUnits'>,
): number | null {
  if (line.unitKind !== 'pack') return line.expectedUnits;
  if (line.packsExpected == null || line.unitsPerPack == null) return null;
  return line.packsExpected * line.unitsPerPack;
}

function normalizeEditValue(value: number | null): number | null {
  if (value == null || !Number.isFinite(value)) return null;
  return Math.trunc(value);
}

/**
 * Applies one merchant edit to a line per the §4.8 table.
 * - expected / packs / unitsPerPack: `acceptedAuto` unchanged; if true, damaged/missing start at 0
 *   when null and accepted = autoAcceptedUnits (an emptied expected makes accepted null).
 * - damaged / missing: `acceptedAuto` unchanged; if true, accepted recomputed (floor 0).
 * - accepted (any value, including the calculated one, or cleared): `acceptedAuto` = false
 *   permanently; accepted = typed value.
 * Any change to packs / unitsPerPack clears `unitsPerPackConfirmed` (§10.2).
 */
export function applyQuantityEdit<L extends QuantityShape>(
  line: L,
  field: ReceivingQuantityField,
  rawValue: number | null,
): L {
  const value = normalizeEditValue(rawValue);

  if (field === 'acceptedUnits') {
    return { ...line, acceptedUnits: value, acceptedAuto: false };
  }

  let next: L = { ...line };
  if (field === 'packsExpected' || field === 'unitsPerPack') {
    next = { ...next, [field]: value, unitsPerPackConfirmed: null };
    next = { ...next, expectedUnits: derivedExpectedUnits(next) };
  } else if (field === 'expectedUnits') {
    next = { ...next, expectedUnits: value };
  } else {
    next = { ...next, [field]: value };
  }

  if (!next.acceptedAuto) return next;

  if (field === 'expectedUnits' || field === 'packsExpected' || field === 'unitsPerPack') {
    if (next.expectedUnits == null) {
      return { ...next, acceptedUnits: null };
    }
    const withDefaults: L = {
      ...next,
      damagedUnits: next.damagedUnits ?? 0,
      missingUnits: next.missingUnits ?? 0,
    };
    return { ...withDefaults, acceptedUnits: autoAcceptedUnits(withDefaults) };
  }

  // damaged / missing on an auto line
  return { ...next, acceptedUnits: autoAcceptedUnits(next) };
}

/** Confirms (or clears) the pack conversion; never touches quantities or provenance. */
export function setPackConfirmation<L extends QuantityShape>(line: L, confirmed: boolean): L {
  if (line.unitKind !== 'pack') return line;
  return { ...line, unitsPerPackConfirmed: confirmed ? line.unitsPerPack : null };
}

/**
 * Scan `incremented` (§4.7): expected + 1; damaged/missing preserved; accepted =
 * autoAcceptedUnits when auto, else accepted + 1; flag unchanged. Returns null when the increment
 * would exceed the quantity limit (caller reports `quantity_limit`).
 */
export function applyScanIncrement<L extends QuantityShape>(line: L): L | null {
  const expected = (line.expectedUnits ?? 0) + 1;
  if (expected > RECEIVING_LIMITS.maxQuantity) return null;
  if (line.acceptedAuto) {
    return applyQuantityEdit(line, 'expectedUnits', expected);
  }
  return { ...line, expectedUnits: expected, acceptedUnits: (line.acceptedUnits ?? 0) + 1 };
}

/** true when accepted + damaged + missing = expected (all present). */
export function quantitiesBalance(
  line: Pick<QuantityShape, 'expectedUnits' | 'acceptedUnits' | 'damagedUnits' | 'missingUnits'>,
): boolean {
  if (
    line.expectedUnits == null ||
    line.acceptedUnits == null ||
    line.damagedUnits == null ||
    line.missingUnits == null
  ) {
    return false;
  }
  return line.acceptedUnits + line.damagedUnits + line.missingUnits === line.expectedUnits;
}
